import styled from "styled-components"
import GridContainer from "@/components/common/GridContainer"
import { getCssSizeValue, getHtmlFromRedactorField, getPaddingsClassFromData } from "@/utils/utils"
import useFilterDataByState from "@/hooks/useFilterDataByState"
import { BREAKPOINTS } from "@/styles/themeSettings"
import { useState } from "react"
import Image from "@/components/common/Image"
import { AnimatePresence, motion } from "framer-motion"

const StyledWrapper = styled(GridContainer)`
	overflow: hidden;
	background: ${({ theme }) => theme.colors.white};
`

const StyledTabsWrapper = styled.div`
	display: flex;
	flex-direction: column;
`

const StyledTabsHeadersWrapper = styled.div`
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	height: fit-content;
`

const StyledTabsHeader = styled.div`
	grid-column: 1 span;
	display: flex;
	justify-content: center;
	align-items: center;
	position: relative;
	white-space: nowrap;
	border: 1px solid ${({ theme }) => theme.colors.richBlack};
	border-bottom: none;
	border-right: none;
	padding: ${getCssSizeValue(19)} 0;
	color: ${({ theme }) => theme.colors.richBlack};
	transition: all 0.3s;

	p {
		position: relative;
		z-index: 2;
	}

	&:last-child {
		border-right: 1px solid ${({ theme }) => theme.colors.richBlack};
	}

	&.active {
		color: ${({ theme }) => theme.colors.white};
	}
`

const ActiveItemIndicator = styled(motion.div)`
	position: absolute;
	top: 0;
	left: 0;
	height: 100%;
	width: 100%;
	background: ${({ theme }) => theme.colors.chilliRed};
`

const StyledTabsContent = styled.div`
	flex-grow: 1;
	border: 1px solid ${({ theme }) => theme.colors.richBlack};
	padding: ${getCssSizeValue(19, BREAKPOINTS.MOBILE)} ${getCssSizeValue(30, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.TABLET]}px) {
		padding: ${getCssSizeValue(19, BREAKPOINTS.TABLET)} ${getCssSizeValue(30, BREAKPOINTS.TABLET)};
	}

	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		padding: ${getCssSizeValue(104, BREAKPOINTS.DESKTOP)} ${getCssSizeValue(40, BREAKPOINTS.DESKTOP)}
			${getCssSizeValue(84, BREAKPOINTS.DESKTOP)};
	}
`

const StyledSmallImageWrapper = styled.div`
	height: auto;
	width: ${getCssSizeValue(150, BREAKPOINTS.DESKTOP)};
	margin-top: ${getCssSizeValue(40, BREAKPOINTS.DESKTOP)};

	.image {
		aspect-ratio: ${({ $aspectRatio }) => ($aspectRatio ? $aspectRatio : 1)};
	}
`

const StyledImageWrapper = styled.div`
	.image {
		height: 100%;
		width: auto;
		aspect-ratio: ${({ $aspectRatio }) => ($aspectRatio ? $aspectRatio : 1)};
	}
`

const variants = {
	enter: {
		opacity: 0,
	},
	animate: {
		opacity: 1,
	},
	exit: {
		opacity: 0,
	},
}

export default function TabsAndImage({ data }) {
	const paddings = getPaddingsClassFromData(data)
	const contextData = useFilterDataByState(data?.tabsAndImage || [])
	const tabs = contextData?.tabs || []
	const [activeItem, setActiveItem] = useState(0)

	return (
		<StyledWrapper className={paddings}>
			<StyledTabsWrapper
				className="col-desktop-8"
				$aspectRatio={(tabs[activeItem]?.bigImage?.width || 1) / (tabs[activeItem]?.bigImage?.height || 1)}
			>
				<StyledTabsHeadersWrapper>
					{tabs.map((item, index) => {
						const title = item?.title || ""
						const isActive = activeItem === index
						return (
							<StyledTabsHeader
								key={item?.id || index}
								onClick={() => setActiveItem(index)}
								className={`${isActive ? "active" : ""}`}
							>
								<p className="typo-strawford typo-strawford-30">{title}</p>
								{isActive && <ActiveItemIndicator layoutId="active-tab-item" />}
							</StyledTabsHeader>
						)
					})}
				</StyledTabsHeadersWrapper>
				<StyledTabsContent>
					<AnimatePresence mode="wait">
						<motion.div
							key={activeItem}
							initial="enter"
							animate="animate"
							exit="exit"
							variants={variants}
							transition={{
								duration: 0.2,
							}}
						>
							<div
								dangerouslySetInnerHTML={getHtmlFromRedactorField(tabs[activeItem]?.description || "")}
								className="typo-strawford typo-strawford-29"
							/>
							<StyledSmallImageWrapper
								$aspectRatio={(tabs[activeItem]?.contentImage?.width || 1) / (tabs[activeItem]?.contentImage?.height || 1)}
							>
								<Image
									src={tabs[activeItem]?.contentImage?.url || ""}
									alt={tabs[activeItem]?.contentImage?.alt || ""}
									fill
									fit
									objectFit="cover"
								/>
							</StyledSmallImageWrapper>
						</motion.div>
					</AnimatePresence>
				</StyledTabsContent>
			</StyledTabsWrapper>
			<StyledImageWrapper
				className="col-desktop-8"
				$aspectRatio={(tabs[activeItem]?.bigImage?.width || 1) / (tabs[activeItem]?.bigImage?.height || 1)}
			>
				<AnimatePresence mode="wait">
					<motion.div
						key={activeItem}
						initial="enter"
						animate="animate"
						exit="exit"
						variants={variants}
						transition={{
							duration: 0.2,
						}}
					>
						<Image
							src={tabs[activeItem]?.bigImage?.url || ""}
							alt={tabs[activeItem]?.bigImage?.alt || ""}
							fit
							objectFit="contain"
						/>
					</motion.div>
				</AnimatePresence>
			</StyledImageWrapper>
		</StyledWrapper>
	)
}
